import { BackrsFormValues, BackrsFormField, AppIcons } from '@backrs_ui/common-ui';

import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

const ICON_MAP: Record<string, AppIcons> = {
  'Agriculture and Animals': 'PiggyBank01',
  'Arts, Entertainment, Media': 'Brush01',
  Business: 'Briefcase01',
  'Education/Children': 'Pencil01',
  'Law/Government': 'Shield01',
  'Patient Care': 'Heart',
  'Science/Tech/Engineering': 'Atom02',
  'Social Sector': 'User03',
  Trades: 'Tool02',
} as const;

export const useGetCareerPathOptions = (): BackrsFormField[] => {
  const { careerCategories, careerPaths } = useAppSelector((state) => state.careerPath);

  const careerPathsInCategory = (categoryId: number) => {
    return careerPaths.filter((path) => path.career_category_id === categoryId);
  };

  return [
    {
      name: 'career_path_category',
      placeholder: 'Career Experience',
      post_type: 'PROFILE_OPTION_ANSWER',
      type: 'text',
      selectOptions: {
        type: 'MULTIPLE',
        renderAsSelect: true,
        options: careerCategories.map((category) => ({
          label: category.name,
          value: category.name,
          icon: ICON_MAP[category.name],
        })),
      },
    },
    ...careerCategories.map(
      (category) =>
        ({
          name: `career_path_${category.id}`,
          placeholder: `What Paths in ${category.name}?`,
          type: 'text',

          post_type: 'PROFILE_OPTION_ANSWER',

          profile_option_category_id: 'career',
          profile_option_option_id: category.name,
          profile_option_suboption_id: 'What subcategory?',
          profile_option_question_id: 'career_fields',

          selectOptions: {
            type: 'MULTIPLE',
            renderAsSelect: true,
            rules: {
              required: 'Please select at least one option',
            },
            options: careerPathsInCategory(category.id).map((path) => ({
              label: path.name,
              value: path.name,
            })),
          },
          showIf: (values: BackrsFormValues) => {
            return values.career_path_category?.includes(category.name);
          },
        }) as BackrsFormField,
    ),
  ];
};

export const defaultSignUpFields: BackrsFormField[] = [
  {
    name: 'first_name',
    placeholder: 'Enter your first name',
    type: 'text',
    post_type: 'USER',
    rules: {
      required: 'First name is required',
    },
  },
  {
    name: 'last_name',
    placeholder: 'Enter your last name',
    type: 'text',
    post_type: 'USER',
    rules: {
      required: 'Last name is required',
    },
  },
  {
    name: 'phone',
    placeholder: 'Add your phone number',
    type: 'tel',
    post_type: 'USER',
    rules: {
      required: 'Phone number is required',
    },
  },
  {
    name: 'email',
    placeholder: 'Enter your email',
    type: 'email',
    post_type: 'USER',
    rules: {
      required: 'Email is required',
    },
  },
  {
    name: 'password',
    placeholder: 'Create a password',
    type: 'password',
    post_type: 'USER',
    rules: {
      required: 'Password is required',
      minLength: { value: 8, message: 'Password must be at least 8 characters long' },
    },
  },
];

export const activationCodeField: BackrsFormField = {
  name: 'invite_code',
  placeholder: 'Activation code (optional)',
  type: 'text',
  post_type: 'USER',
};

export const linkedInField: BackrsFormField = {
  name: 'linkedin_url',
  placeholder: 'Add your profile',
  customStartAdornment: 'linkedin.com/in/',
  type: 'text',
  post_type: 'PROFILE_OPTION_ANSWER',

  profile_option_category_id: 'linkedIn',
  profile_option_question_id: 'linkedIn_url',
};

export const schoolField: BackrsFormField = {
  name: 'school',
  placeholder: 'Enter your school',
  type: 'SINGLE',
  autoCompleteEndpoint: '/profile/lookups/schools',

  post_type: 'PROFILE_OPTION_ANSWER',

  profile_option_category_id: 'school',
  profile_option_option_id: 'school',
  profile_option_question_id: 'school',

  selectOptions: {
    type: 'MULTIPLE',
    renderAsSelect: true,
    options: [],
  },
};

export const zipCodeField: BackrsFormField = {
  name: 'zip_code',
  placeholder: 'Enter your zip code',
  type: 'zip',
  post_type: 'PROFILE_OPTION_ANSWER',

  profile_option_category_id: 'identity',
  profile_option_question_id: 'zip_code',
};
